import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { createId } from '@paralleldrive/cuid2';
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMessage,
  faUser,
  faComments,
  faSync,
  faSignOutAlt,
  faPowerOff,
  faTrash,
  faQrcode,
  faChevronDown,
  faChevronUp,
  faSpinner,
  faUpload
} from "@fortawesome/free-solid-svg-icons";
import useAppSession from "../../stores/useAppSession";
import apiWhatsapp from "../../services/api/ApiWhatsapEvolution";
import { jwtDecode } from "jwt-decode";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 40px 24px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 32px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin: 24px 0 16px;
`;

const CardTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 500;
  color: #666;
  margin-bottom: 6px;
`;

const StyledInput = styled.input`
  height: 40px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 0 12px;
  color: #333;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff8f40;
  }

  &:disabled {
    background: #f9f9f9;
    color: #999;
    cursor: not-allowed;
  }
`;

const PrefixInputContainer = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding-left: 12px;
  
  span {
    color: #999;
    font-size: 15px;
    padding-right: 4px;
  }

  input {
    border: none;
    flex: 1;
    &:focus { border: none; }
  }
`;

const ActionButton = styled.button`
  height: 40px;
  padding: 0 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s;
  border: 1px solid transparent;

  background: ${props => {
    if (props.variant === 'primary') return '#ff8f40';
    if (props.variant === 'danger') return '#ff4d4f';
    return '#ffffff';
  }};
  
  color: ${props => props.variant === 'outline' ? '#333' : '#ffffff'};
  border-color: ${props => props.variant === 'outline' ? '#eaeaea' : 'transparent'};

  &:hover { opacity: 0.9; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

const StatusTag = styled.span`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => props.color === 'green' ? '#f6ffed' : '#fff2f0'};
  color: ${props => props.color === 'green' ? '#52c41a' : '#ff4d4f'};
  border: 1px solid currentColor;
`;

const StatsGrid = styled.div`
  display: flex;
  gap: 24px;
  margin: 20px 0;
  font-size: 15px;

  span {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #666;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;

  input { opacity: 0; width: 0; height: 0; }

  span {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 22px;
  }

  span:before {
    position: absolute;
    content: "";
    height: 16px; width: 16px;
    left: 3px; bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }

  input:checked + span { background-color: #ff8f40; }
  input:checked + span:before { transform: translateX(18px); }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #333;
  cursor: pointer;
  input { accent-color: #ff8f40; }
`;

const CollapseHeader = styled.div`
  padding: 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: #333;
  font-weight: 600;
  font-size: 15px;
  border-top: 1px solid #eaeaea;
  margin-top: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
`;

const ModalBox = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 500px;
  border-radius: 6px;
  padding: 32px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  font-size: 15px;
  color: #666;
`;

const eventsList = [
  { label: "Início da aplicação", value: "APPLICATION_STARTUP" },
  { label: "Chamada", value: "CALL" },
  { label: "Excluir Conversas", value: "CHATS_DELETE" },
  { label: "Configurar Conversas", value: "CHATS_SET" },
  { label: "Atualizar Conversas", value: "CHATS_UPDATE" },
  { label: "Adicionar/Atualizar Conversas", value: "CHATS_UPSERT" },
  { label: "Atualização da Conexão", value: "CONNECTION_UPDATE" },
  { label: "Contatos Configurados", value: "CONTACTS_SET" },
  { label: "Atualizar Contatos", value: "CONTACTS_UPDATE" },
  { label: "Adicionar/Atualizar Contatos", value: "CONTACTS_UPSERT" },
  { label: "Participantes do Grupo Atualizados", value: "GROUP_PARTICIPANTS_UPDATE" },
  { label: "Grupo Atualizado", value: "GROUP_UPDATE" },
  { label: "Adicionar/Atualizar Grupos", value: "GROUPS_UPSERT" },
  { label: "Associação de Labels", value: "LABELS_ASSOCIATION" },
  { label: "Editar Labels", value: "LABELS_EDIT" },
  { label: "Logout da Instância", value: "LOGOUT_INSTANCE" },
  { label: "Excluir Mensagens", value: "MESSAGES_DELETE" },
  { label: "Configurar Mensagens", value: "MESSAGES_SET" },
  { label: "Atualizar Mensagens", value: "MESSAGES_UPDATE" },
  { label: "Adicionar/Atualizar Mensagens", value: "MESSAGES_UPSERT" },
  { label: "Atualização de Presença", value: "PRESENCE_UPDATE" },
  { label: "QR Code Atualizado", value: "QRCODE_UPDATED" },
  { label: "Remover Instância", value: "REMOVE_INSTANCE" },
  { label: "Enviar Mensagem", value: "SEND_MESSAGE" },
  { label: "Alteração Status do TypeBot", value: "TYPEBOT_CHANGE_STATUS" },
  { label: "Início do TypeBot", value: "TYPEBOT_START" },
];

function decode(token) {
  try { return jwtDecode(token); } catch { return null; }
}

function generateInstanceName(token, instanceName) {
  const decoded = decode(token);
  if (!decoded) return instanceName;
  const workspaceName = decoded.user_access?.environment?.workspace?.name || "workspace";
  const cleanWorkspace = workspaceName.replace(/[^a-zA-Z0-9]/g, "");
  const cleanInstance = instanceName.replace(/[^a-zA-Z0-9]/g, "");
  return `${cleanWorkspace}-${cleanInstance}`;
}

const WhatsappConnection = () => {
  const [formData, setFormData] = useState({
    name: "", number: "", token: "", rejectCall: true, msgCall: "Olá este canal só aceita mensagens, em que posso te ajudar?",
    ignoreGroups: true, alwaysOnline: true, readMessages: true, syncFullHistory: false, readStatus: true,
    webhookUrl: "", webhookEnabled: false, webhookByEvents: false, webhookEvents: []
  });

  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instance, setInstance] = useState(null);
  const [checking, setChecking] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [connectionState, setConnectionState] = useState(null);
  const [expandedPanels, setExpandedPanels] = useState({ webhook: false, settings: false });

  const { token } = useAppSession();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEventToggle = (val) => {
    setFormData(prev => {
      const events = prev.webhookEvents.includes(val) ? prev.webhookEvents.filter(i => i !== val) : [...prev.webhookEvents, val];
      return { ...prev, webhookEvents: events };
    });
  };

  const fetchInstance = async () => {
    try {
      setChecking(true);
      const decoded = decode(token);
      const workspaceName = decoded?.user_access?.environment?.workspace?.name || "workspace";
      const res = await apiWhatsapp.getInstances();

      if (res.data?.length) {
        const filteredInstances = res.data.filter(inst => inst.name.startsWith(`${workspaceName}-`));
        const selectedInstance = filteredInstances.length ? filteredInstances.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0] : null;

        if (selectedInstance) {
          setInstance(selectedInstance);
          const conn = await apiWhatsapp.connectionStateInstances(selectedInstance.name);
          setConnectionState(conn.data?.instance?.state || null);
          const webhookExternal = await apiWhatsapp.getWebhookExternal(selectedInstance.name);

          setFormData({
            name: selectedInstance.name,
            number: selectedInstance.number,
            token: selectedInstance.token,
            rejectCall: selectedInstance.Setting?.rejectCall ?? true,
            msgCall: selectedInstance.Setting?.msgCall ?? "Olá este canal só aceita mensagens, em que posso te ajudar?",
            ignoreGroups: selectedInstance.Setting?.groupsIgnore ?? true,
            alwaysOnline: selectedInstance.Setting?.alwaysOnline ?? true,
            readMessages: selectedInstance.Setting?.readMessages ?? true,
            syncFullHistory: selectedInstance.Setting?.syncFullHistory ?? false,
            readStatus: selectedInstance.Setting?.readStatus ?? true,
            webhookUrl: webhookExternal?.url || process.env.REACT_APP_WHATSAPP_EVOLUTION || "",
            webhookEnabled: webhookExternal?.enabled ?? false,
            webhookByEvents: webhookExternal?.webhookByEvents ?? false,
            webhookEvents: webhookExternal?.events || [],
          });
        }
      }
    } catch (err) {
      toast.error("Erro ao buscar instâncias: " + err.message);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { fetchInstance(); }, []);

  const handleUpdateSettings = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      const payload = {
        rejectCall: formData.rejectCall,
        msgCall: formData.msgCall,
        groupsIgnore: formData.ignoreGroups,
        alwaysOnline: formData.alwaysOnline,
        readMessages: formData.readMessages,
        syncFullHistory: formData.syncFullHistory,
        readStatus: formData.readStatus,
      };
      await apiWhatsapp.postSettings(instance.name, payload);
      toast.success("Configurações atualizadas!");
    } catch (err) {
      toast.error("Erro ao atualizar settings: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQr = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      const res = await apiWhatsapp.connectInstances(instance.name);
      if (res.data?.base64) {
        setQrCode(res.data.base64);
        setQrModalVisible(true);
        startPollingQr(instance.name);
      }
    } catch (err) {
      toast.error("Erro QR: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const startPollingQr = (instanceName) => {
    let elapsed = 0;
    const interval = setInterval(async () => {
      elapsed += 2000;
      try {
        const res = await apiWhatsapp.connectionStateInstances(instanceName);
        if (res.data?.instance.state === "open") {
          fetchInstance();
          toast.success("WhatsApp conectado!");
          clearInterval(interval);
          setQrModalVisible(false);
        } else if (elapsed >= 30000) {
          clearInterval(interval);
          setQrModalVisible(false);
        }
      } catch (err) { clearInterval(interval); }
    }, 2000);
  };

  const handleCreateInstance = async () => {
    if (!formData.name || !formData.number) {
      toast.error("Informe Nome e Número.");
      return;
    }
    try {
      setLoading(true);
      await apiWhatsapp.createInstance({
        instanceName: generateInstanceName(token, formData.name),
        number: formData.number,
        token: formData.token || createId(),
        integration: "WHATSAPP-BAILEYS",
        webhook: {
          url: process.env.REACT_APP_WEBHOOK_WHATSAPP_EVOLUTION || "",
          byEvents: false,
          base64: false,
          events: ["MESSAGES_UPSERT"]
        }
      });
      toast.success("Instância criada!");
      fetchInstance();
    } catch (err) {
      toast.error("Erro ao criar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      await apiWhatsapp.logoutInstance(instance.name);
      toast.success("Logout realizado!");
      setConnectionState("close");
      fetchInstance();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleRestart = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      await apiWhatsapp.restartInstance(instance.name);
      toast.success("Instância reiniciada!");
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleReconnect = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      await apiWhatsapp.connectInstances(instance.name);
      toast.success("Reconectando...");
      fetchInstance();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      await apiWhatsapp.deleteInstance(instance.name);
      toast.success("Instância deletada!");
      setInstance(null);
      setConnectionState(null);
      setFormData({ name: "", number: "", token: "", rejectCall: true, msgCall: "", ignoreGroups: true, alwaysOnline: true, readMessages: true, syncFullHistory: false, readStatus: true, webhookUrl: "", webhookEnabled: false, webhookByEvents: false, webhookEvents: [] });
    } catch (err) { toast.error(err.message); } finally { setLoading(false); setDeleteModalVisible(false); }
  };

  const handleWebhookUpdate = async () => {
    if (!instance) return;
    try {
      setLoading(true);
      await apiWhatsapp.updateWebhookOptions(instance.name, {
        enabled: formData.webhookEnabled,
        url: formData.webhookUrl,
        events: formData.webhookEvents,
        base64: false,
        byEvents: formData.webhookByEvents,
      });
      toast.success("Webhook atualizado!");
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  if (checking || loading) {
    return (
      <PageWrapper>
        <MainCard>
          <Loader>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />
            <span>Processando informações...</span>
          </Loader>
        </MainCard>
      </PageWrapper>
    );
  }

  const decodedToken = decode(token);
  const currentWorkspace = decodedToken?.user_access?.environment?.workspace?.name || "workspace";
  const isClosed = connectionState === "close";

  return (
    <PageWrapper>
      <MainCard>
        <Title>Conectar Ao WhatsApp</Title>

        {!instance ? (
          <>
            <SectionTitle>Criar Nova Instância</SectionTitle>
            <FormGroup>
              <Label>Nome da Instância</Label>
              <PrefixInputContainer>
                <span>{currentWorkspace}-</span>
                <StyledInput name="name" value={formData.name} onChange={handleInputChange} placeholder="Nome" />
              </PrefixInputContainer>
              <Label>Número do WhatsApp</Label>
              <StyledInput name="number" value={formData.number} onChange={handleInputChange} placeholder="Ex: 5511..." />
              <Label>Token (opcional)</Label>
              <StyledInput name="token" value={formData.token} onChange={handleInputChange} />
              <ActionButton variant="primary" onClick={handleCreateInstance}>Criar Instância</ActionButton>
            </FormGroup>
          </>
        ) : (
          <>
            <SectionTitle>Informações da Instância</SectionTitle>
            <FormGroup>
              <Label>Nome: {formData.name}</Label>
              <Label>Número: {formData.number}</Label>
            </FormGroup>

            <StatsGrid>
              <StatusTag color={instance.connectionStatus === "open" && !isClosed ? "green" : "volcano"}>
                {isClosed ? "Fechado" : instance.connectionStatus === "open" ? "Conectado" : "Desconectado"}
              </StatusTag>
              <span><FontAwesomeIcon icon={faMessage} /> {instance._count?.Message || 0}</span>
              <span><FontAwesomeIcon icon={faUser} /> {instance._count?.Contact || 0}</span>
              <span><FontAwesomeIcon icon={faComments} /> {instance._count?.Chat || 0}</span>
            </StatsGrid>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {isClosed ? (
                <ActionButton variant="primary" onClick={handleReconnect}>Login</ActionButton>
              ) : (
                <>
                  <ActionButton variant="danger" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                  </ActionButton>
                  <ActionButton variant="outline" onClick={handleRestart}>
                    <FontAwesomeIcon icon={faPowerOff} /> Reiniciar
                  </ActionButton>
                  {instance.connectionStatus !== "open" && (
                    <ActionButton variant="primary" onClick={handleGenerateQr}>
                      <FontAwesomeIcon icon={faQrcode} /> Escanear QR
                    </ActionButton>
                  )}
                </>
              )}
            </div>

            <CollapseHeader onClick={() => setExpandedPanels(p => ({ ...p, webhook: !p.webhook }))}>
              Configurações do Webhook
              <FontAwesomeIcon icon={expandedPanels.webhook ? faChevronUp : faChevronDown} />
            </CollapseHeader>
            {expandedPanels.webhook && (
              <FormGroup style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Label style={{ margin: 0 }}>Ativar</Label>
                    <ToggleSwitch>
                      <input type="checkbox" name="webhookEnabled" checked={formData.webhookEnabled} onChange={handleInputChange} />
                      <span />
                    </ToggleSwitch>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Label style={{ margin: 0 }}>Eventos</Label>
                    <ToggleSwitch>
                      <input type="checkbox" name="webhookByEvents" checked={formData.webhookByEvents} onChange={handleInputChange} />
                      <span />
                    </ToggleSwitch>
                  </div>
                </div>
                <StyledInput name="webhookUrl" value={formData.webhookUrl} onChange={handleInputChange} placeholder="URL do Webhook" />
                <CheckboxGrid>
                  {eventsList.map(ev => (
                    <CheckboxItem key={ev.value}>
                      <input type="checkbox" checked={formData.webhookEvents.includes(ev.value)} onChange={() => handleEventToggle(ev.value)} />
                      {ev.label}
                    </CheckboxItem>
                  ))}
                </CheckboxGrid>
                <ActionButton variant="primary" onClick={handleWebhookUpdate}>Atualizar Webhook</ActionButton>
              </FormGroup>
            )}

            <CollapseHeader onClick={() => setExpandedPanels(p => ({ ...p, settings: !p.settings }))}>
              Preferências da Instância
              <FontAwesomeIcon icon={expandedPanels.settings ? faChevronUp : faChevronDown} />
            </CollapseHeader>
            {expandedPanels.settings && (
              <FormGroup style={{ marginTop: '16px' }}>
                {[
                  { label: "Rejeitar chamadas", name: "rejectCall" },
                  { label: "Ignorar grupos", name: "ignoreGroups" },
                  { label: "Sempre online", name: "alwaysOnline" },
                  { label: "Ler mensagens", name: "readMessages" },
                  { label: "Sincronizar histórico", name: "syncFullHistory" }
                ].map(item => (
                  <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Label style={{ margin: 0 }}>{item.label}</Label>
                    <ToggleSwitch>
                      <input type="checkbox" name={item.name} checked={formData[item.name]} onChange={handleInputChange} />
                      <span />
                    </ToggleSwitch>
                  </div>
                ))}
                <Label>Mensagem de rejeição</Label>
                <StyledInput name="msgCall" value={formData.msgCall} onChange={handleInputChange} />
                <ActionButton variant="primary" onClick={handleUpdateSettings}>Atualizar Configurações</ActionButton>
              </FormGroup>
            )}

            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <ActionButton variant="danger" style={{ margin: '0 auto' }} onClick={() => setDeleteModalVisible(true)}>
                <FontAwesomeIcon icon={faTrash} /> Deletar Instância Permanente
              </ActionButton>
            </div>
          </>
        )}
      </MainCard>

      {qrModalVisible && (
        <ModalOverlay onClick={() => setQrModalVisible(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <CardTitle>Escanear QR Code</CardTitle>
            {qrCode ? (
              <img src={qrCode} alt="QR" style={{ width: '100%', borderRadius: '6px' }} />
            ) : <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />}
            <ActionButton variant="outline" style={{ marginTop: '20px', width: '100%' }} onClick={() => setQrModalVisible(false)}>Fechar</ActionButton>
          </ModalBox>
        </ModalOverlay>
      )}

      {deleteModalVisible && (
        <ModalOverlay onClick={() => setDeleteModalVisible(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <CardTitle>Confirmar Exclusão</CardTitle>
            <p style={{ fontSize: '15px', color: '#666', margin: '16px 0' }}>Deseja realmente deletar esta instância? Esta ação não pode ser desfeita.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <ActionButton variant="outline" style={{ flex: 1 }} onClick={() => setDeleteModalVisible(false)}>Cancelar</ActionButton>
              <ActionButton variant="danger" style={{ flex: 1 }} onClick={handleDelete}>Confirmar</ActionButton>
            </div>
          </ModalBox>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
};

export default WhatsappConnection;